import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { FaProjectDiagram } from 'react-icons/fa';
import { RiHandCoinLine } from 'react-icons/ri';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

import BreadCrumb from '~/components/BreadCrumb';
import CurrencyInput from '~/components/Inputs/Currency';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import DefaultInput from '~/components/Inputs/Default';
import ExitButton from '~/components/Buttons/Exit';
import NewProjectInfo from '~/components/ProjectInfo/New';
import NoticeDialog from '~/components/Dialogs/Notice';
import PhoneInput from '~/components/Inputs/Phone';
import SaveButton from '~/components/Buttons/Save';
import Select from '~/components/Select';
import TextArea from '~/components/TextArea';
import UploadFiles from '~/components/UploadFiles';
import UploadRequestFiles from '~/components/UploadRequestFiles';

import { getCurrencyFormatValue, getFormattedCurrency } from '~/utils';

import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Content, Wrapper, Label } from './styles';

interface IFormProcedure1 {
  rebate_balance: {
    value: string;
  };
  price: number;
  client_contact_name: string;
  client_contact_phone: string;
  client_contact_email: string;
  note: string;
}

interface ILocation {
  erp_client_code?: string;
  erp_client_name?: string;
  erp_client_company_name?: string;
  erp_client_document?: string;
  erp_client_address?: string;
  erp_client_city?: string;
  erp_client_state?: string;
  erp_rep_code: string;
  rep_as_client: boolean;
}

const schema = Yup.object().shape({
  rebate_balance: Yup.object().shape({
    value: Yup.string().required('Campo obrigatório'),
  }),
  price: Yup.string().required('Campo obrigatório'),
  client_contact_name: Yup.string().required('Campo obrigatório'),
  client_contact_phone: Yup.string()
    .required('Campo obrigatório')
    .min(14, 'Telefone/celular inválido')
    .max(15, 'Telefone/celular inválido'),
  client_contact_email: Yup.string()
    .required('Campo obrigatório')
    .email('E-mail inválido'),
});

const Procedure1: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const projectTemp = useRef(uuidv4());
  const projectRequestTemp = useRef(uuidv4());
  const [rebateBalances, setRebateBalances] = useState<
    {
      id: string;
      pending_price: number;
      quarter: {
        code: string;
      };
      policy_group: {
        name: string;
      };
    }[]
  >();
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpenNotice, setIsOpenNotice] = useState(false);

  const { handleSubmit, register, getValues, setValue, control, errors } =
    useForm<IFormProcedure1>({ resolver: yupResolver(schema) });

  useEffect(() => {
    const checkEnabledFunctionality = async () => {
      await api.get('/generalsettings').then(response => {
        if (response.data && !response.data.new_rebate_req_enabled) {
          history.push('/');
        }
      });
    };

    checkEnabledFunctionality();
  }, [history]);

  useEffect(() => {
    if (state === undefined) {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Cliente não selecionado',
      });

      history.push('/projects/rebate');
    } else {
      api
        .get(`/rebatebalances/available?client_code=${state.erp_client_code}`)
        .then(response => {
          setRebateBalances(response.data);
        });
    }
  }, [addToast, history, state, user, register, setValue]);

  const submitForm = useCallback(
    async (data: IFormProcedure1) => {
      try {
        setIsSaving(true);

        await api.post('/projects', {
          ...data,
          price: getCurrencyFormatValue(data.price),
          project_request_temp_id: projectRequestTemp.current,
          project_temp_id: projectTemp.current,
          erp_client_code: state.erp_client_code,
          erp_rep_code: state.erp_rep_code,
          record_note: data.note,
          record_description: 'Abertura de solicitação',
          rep_as_client: state.rep_as_client,
          type: 'REBATE',
        });

        addToast({
          type: 'success',
          title: 'Solicitação iniciada!',
          description: 'Abertura de solicitação realizada com sucesso.',
        });

        history.push('/projects/rebate');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, projectRequestTemp, projectTemp, state],
  );

  const onExit = () => {
    api.delete(`projectfilesreceived/projecttempid/${projectTemp.current}`);
    api.delete(
      `projectrequestfilesreceived/projectrequesttempid/${projectRequestTemp.current}`,
    );

    history.push('/projects/rebate');
  };

  const handleFiles = (): React.ReactNode | null => {
    if (user.user_type.type !== 'REP') {
      return (
        <Content>
          <UploadFiles
            type="anexo"
            project_temp_id={projectTemp.current}
            project_type="REBATE"
            sequence={1}
            user_id={user.id}
          />
        </Content>
      );
    }

    return null;
  };

  return (
    <Container>
      <BreadCrumb
        main="Solicitações"
        path="/projects/main"
        firstChildPath="/projects/rebate"
        firstChild="Rebate"
        secondChild="Nova Solicitação"
        mainIcon={FaProjectDiagram}
        firstChildIcon={RiHandCoinLine}
        secondChildIcon={RiHandCoinLine}
        mainColored
        firstChildColored
      />

      <NewProjectInfo
        rep_as_client={state?.rep_as_client}
        erp_rep_code={state?.erp_rep_code}
        erp_client_code={state?.erp_client_code}
      />

      <form onSubmit={handleSubmit(submitForm)}>
        <div
          style={{
            maxWidth: 680,
            marginBottom: 20,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <Wrapper style={{ width: 320 }}>
            <Controller
              name="rebate_balance"
              control={control}
              options={rebateBalances
                ?.sort(
                  (a, b) =>
                    a.quarter.code.localeCompare(b.quarter.code) ||
                    a.policy_group.name.localeCompare(b.policy_group.name),
                )
                .map(r_b => ({
                  value: r_b.id,
                  label:
                    `(${r_b.quarter.code}) - ` +
                    `(${r_b.policy_group.name}) - ` +
                    `Disponível: ${getFormattedCurrency(r_b.pending_price)}`,
                }))}
              label="Grupo de Pol. Com. / Trimestre"
              error={errors?.rebate_balance?.value?.message}
              as={Select}
              loading={!rebateBalances}
            />
          </Wrapper>

          <Wrapper style={{ width: 320 }}>
            <CurrencyInput
              name="price"
              labelFor="price"
              labelText="Valor"
              defaultValue={0}
              error={errors?.price?.message}
              ref={register}
            />
          </Wrapper>
        </div>

        <Label>Contato da Loja</Label>

        <div
          style={{
            maxWidth: 680,
            marginBottom: 20,
            padding: 10,
            borderRadius: '0.4rem',
            border: '1px solid #e4e4e4',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <Wrapper style={{ width: 390 }}>
            <DefaultInput
              name="client_contact_name"
              labelFor="client_contact_name"
              labelText="Nome"
              type="text"
              error={errors?.client_contact_name?.message}
              ref={register}
              maxLength={60}
            />
          </Wrapper>

          <Wrapper style={{ width: 250 }}>
            <PhoneInput
              name="client_contact_phone"
              labelFor="client_contact_phone"
              labelText="Telefone"
              type="text"
              error={errors?.client_contact_phone?.message}
              ref={register}
            />
          </Wrapper>

          <Wrapper style={{ width: 650 }}>
            <DefaultInput
              name="client_contact_email"
              labelFor="client_contact_email"
              labelText="E-mail"
              type="text"
              error={errors?.client_contact_email?.message}
              ref={register}
              maxLength={60}
            />
          </Wrapper>
        </div>

        <Content>
          <Wrapper style={{ width: '100%', marginBottom: 20 }}>
            <TextArea
              name="note"
              labelFor="note"
              labelText="Observação"
              error={errors?.note?.message}
              ref={register}
              maxLength={1000}
            />
          </Wrapper>
        </Content>

        <Content>
          <UploadRequestFiles
            type="requisicao"
            project_request_temp_id={projectRequestTemp.current}
            project_type="REBATE"
            sequence={1}
            user_id={user.id}
          />
        </Content>

        {handleFiles()}

        <div
          style={{
            margin: 20,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              const insertedPrice = getCurrencyFormatValue(getValues('price'));

              const balancePendingPrice =
                rebateBalances?.find(
                  r_b => r_b.id === getValues('rebate_balance')?.value,
                )?.pending_price || 0;

              if (balancePendingPrice < insertedPrice) {
                setIsOpenNotice(true);
              } else {
                setIsOpenConfirmProcedure(true);
              }
            }}
          />

          <ExitButton
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title="Confirma abertura da solicitação?"
        subTitle="A solicitação será enviada para o próximo procedimento"
        opConfirm="Sim"
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />

      <NoticeDialog
        isOpen={isOpenNotice}
        type="error"
        title="Valor Indevido"
        subTitle="O valor informado é maior do que o saldo disponível para rebate."
        setIsOpen={setIsOpenNotice}
      />
    </Container>
  );
};

export default Procedure1;
